(function ($) {
    // ====== Fresh-nonce AJAX helper ======
    // Fetches the cart nonce on demand from a non-cacheable REST route so page
    // cache can never serve a stale nonce baked into the HTML (which produced a
    // 403 from check_ajax_referer for logged-out visitors on cached pages).
    const NONCE_ENDPOINT = '/wp-json/dnnz/v1/nonce';
    let cachedNonce = null;
    let inflightNonce = null;

    function fetchNonce() {
        return fetch(NONCE_ENDPOINT, {
            cache: 'no-store',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
        }).then(function (res) {
            if (!res.ok) throw new Error('Nonce fetch failed: ' + res.status);
            return res.json();
        }).then(function (data) {
            if (!data || typeof data.nonce !== 'string') throw new Error('Nonce response malformed');
            return data.nonce;
        });
    }

    function getNonce() {
        if (cachedNonce) return Promise.resolve(cachedNonce);
        if (inflightNonce) return inflightNonce;
        inflightNonce = fetchNonce().then(function (nonce) {
            cachedNonce = nonce;
            return nonce;
        }).finally(function () {
            inflightNonce = null;
        });
        return inflightNonce;
    }

    // POST to admin-ajax. Fast path: use the nonce baked into the page (busted per
    // deploy via the theme version), so the happy path costs zero extra requests.
    // Only on a 403 — a stale baked nonce, e.g. a page-cache TTL longer than the
    // nonce lifetime — fetch a fresh one from the non-cacheable endpoint and retry
    // once. Falls back to the endpoint up front only if no nonce was localized.
    function dnnzAjax(action, payload) {
        const ajaxUrl = offer35_data.ajax_url;
        const buildBody = function (nonce) {
            const body = new URLSearchParams();
            Object.keys(payload || {}).forEach(function (key) {
                body.append(key, String(payload[key]));
            });
            body.set('action', action);
            body.set('security', nonce);
            return body;
        };
        const post = function (nonce) {
            return fetch(ajaxUrl, { method: 'POST', credentials: 'same-origin', body: buildBody(nonce) });
        };
        const baked = (offer35_data && typeof offer35_data.nonce === 'string') ? offer35_data.nonce : null;
        // Prefer an already-fetched fresh nonce, then the baked-in one; hit the
        // endpoint up front only when neither exists.
        const firstNonce = cachedNonce || baked;
        const firstAttempt = firstNonce ? post(firstNonce) : getNonce().then(post);
        return firstAttempt.then(function (response) {
            if (response.status !== 403) return response;
            cachedNonce = null;
            return getNonce().then(post);
        });
    }

    function getMonthsWord(months) {
        if (months === 1) return 'miesiąc';
        if (months >= 2 && months <= 4) return 'miesiące';
        return 'miesięcy';
    }

    function formatPrice(price) {
        price = (Number.isInteger(price)) ? price : price.toFixed(2);
        return price.toString().replace('.', ',');
    }

    function getActivePaymentType($block) {
        const $active = $block.find('#select-payment-type li.active');
        return $active.length > 0 ? $active.data('paymentType') : 'subscription';
    }

    function updatePaymentTypeMessage($block, paymentType) {
        const $messageBox = $block.find('.payment-type-message');
        if ($messageBox.length === 0) return;

        const subscriptionMessage = $messageBox.data('subscriptionMessage');
        const onetimeMessage = $messageBox.data('onetimeMessage');
        const $activeVariant = $block.find('#select-variant li.active');
        const variantLength = parseInt($activeVariant.data('variant-length')) || 1;

        let message = paymentType === 'subscription' ? subscriptionMessage : onetimeMessage;
        if (paymentType === 'subscription' && message) {
            message = message.replace('{months}', variantLength);
            message = message.replace('{months_word}', getMonthsWord(variantLength));
        }
        $messageBox.html(message);
    }

    function getActiveVariantData($column, paymentType) {
        const subVariants = $column.data('subscriptionVariants') || {};
        const oneVariants = $column.data('onetimeVariants') || {};
        if (paymentType === 'subscription' && Object.keys(subVariants).length) {
            return subVariants;
        }
        if (Object.keys(oneVariants).length) {
            return oneVariants;
        }
        return subVariants;
    }

    function refreshColumn($block, $column) {
        const $activeVariant = $block.find('#select-variant li.active');
        const variantId = parseInt($activeVariant.data('variant')) || 0;
        const variantLength = parseInt($activeVariant.data('variant-length')) || 1;
        const showPromo = !!$block.data('showPromoPrices');
        const paymentType = getActivePaymentType($block);
        const variants = getActiveVariantData($column, paymentType);
        const variant = variants[variantId];

        // Promo badge (per payment type + variant).
        const $promoBadge = $column.find('.promo-badge');
        if ($promoBadge.length) {
            const promoBadges = $promoBadge.data('promoBadges') || {};
            const promoHtml = (promoBadges[paymentType] || {})[variantId] || '';
            $promoBadge.html(promoHtml).toggleClass('hidden', !promoHtml);
        }

        if (!variant) return;

        const price = parseFloat(variant.price);
        const regularPrice = parseFloat(variant.display_regular_price);
        const isOnSale = regularPrice > price;
        const pricePerMonth = Math.round(price / variantLength);
        const regularPerMonth = Math.round(regularPrice / variantLength);

        // Price total (large) — value + optional <del>regular</del> + suffix "zł" / "zł za N mies.".
        const $priceTotal = $column.find('.price-total');
        if ($priceTotal.length) {
            const $totalStrong = $priceTotal.find('strong');
            const $totalDel = $priceTotal.find('del');
            const $totalSuffix = $priceTotal.find('.suffix');
            $totalStrong.text(formatPrice(price));
            $totalSuffix.text(variantLength > 1 ? `zł za ${variantLength} mies.` : 'zł');
            if (showPromo && isOnSale) {
                $totalDel.text(formatPrice(regularPrice)).show();
            } else {
                $totalDel.hide();
            }
        }

        // Price per month (small line, only when variant > 1 month).
        const $priceMonth = $column.find('.price-month');
        if ($priceMonth.length) {
            if (variantLength > 1) {
                $priceMonth.find('strong').text(formatPrice(pricePerMonth));
                $priceMonth.find('.suffix').text($column.data('priceMonthSuffix') || 'zł/mies.');
                $priceMonth.show();
            } else {
                $priceMonth.hide();
            }
        }

        // Renewal info (only relevant for subscription, CSS hides on onetime).
        const $renewal = $column.find('.renewal-info');
        if ($renewal.length && variantLength > 0) {
            const template = $renewal.attr('data-template') || '';
            if (template) {
                const monthsWord = getMonthsWord(variantLength);
                $renewal.text(
                    template
                        .replace('{months}', variantLength)
                        .replace('{months_word}', monthsWord)
                        .replace('{promo_price}', formatPrice(price))
                );
            }
        }

        // CTA add-to-cart link.
        const $cta = $column.find('.offer-35__add-to-cart');
        if ($cta.length) {
            $cta.attr('href', `?add-to-cart=${variant.variation_id}`);
            $cta.attr('data-product_id', variant.variation_id);
            const ctaText = paymentType === 'onetime'
                ? ($cta.attr('data-cta-onetime') || $cta.attr('data-cta-subscription'))
                : ($cta.attr('data-cta-subscription') || $cta.attr('data-cta-onetime'));
            if (ctaText) {
                $cta.text(ctaText);
            }
        }

        // Savings — compare 1-month variant vs current variant within the active payment type.
        const baseVariant = variants[0];
        const savingsVariant = variants[variantId];
        if (baseVariant && savingsVariant) {
            const savingsRaw = parseFloat(baseVariant.price) * variantLength - parseFloat(savingsVariant.price);
            const savings = formatPrice(savingsRaw);
            const $savings = $column.find('.package_savings');
            const proto = $savings.data('prototype') || '';
            if (savingsRaw > 0 && proto) {
                $savings.text(proto.replace('%s', savings)).removeClass('hidden');
            } else {
                $savings.addClass('hidden');
            }
        }

        // Omnibus.
        const $omnibus = $column.find('.omnibus');
        $omnibus.html(variant.omnibus_msg || '');

        // Properties show_on filter.
        $column.find('.property').each(function () {
            const showOn = $(this).data('showOn');
            const list = Array.isArray(showOn) ? showOn.map(String) : [];
            if (list.includes(variantId.toString())) {
                $(this).removeClass('hidden');
            } else {
                $(this).addClass('hidden');
            }
        });

        // GTM.
        const variantsGtm = $column.data('gtm4wpData');
        if (variantsGtm && variantsGtm[variantId]) {
            $column.find('.gtm4wp_productdata').attr('data-gtm4wp_product_data', variantsGtm[variantId]);
        }
    }

    function refreshBlock($block) {
        $block.find('.offer-35__column').each(function () {
            refreshColumn($block, $(this));
        });

        // Update package-info.
        const $info = $block.find('.package-info');
        if ($info.length) {
            const descriptions = $info.data('descriptions') || [];
            const variantId = parseInt($block.find('#select-variant li.active').data('variant')) || 0;
            if (descriptions[variantId] !== undefined) {
                $info.html(descriptions[variantId]);
            }
        }

        // Sync data-payment-type on block wrapper for CSS rules.
        const paymentType = getActivePaymentType($block);
        $block.attr('data-payment-type', paymentType);

        // Recompute equal-height min-heights since renewal-info/price-month visibility changes.
        adjustColumnTopHeights();
    }

    function scrollToColumns($block) {
        if (window.innerWidth >= 1200) return;
        if (!$block.find('.package-select-wrapper').hasClass('stuck')) return;
        const columns = $block.find('.offer-35__columns')[0];
        if (!columns) return;
        columns.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function initBlock($block) {
        // Variant click.
        $block.find('#select-variant li').on('click', function (e) {
            e.preventDefault();
            $(this).parent().find('li').removeClass('active');
            $(this).addClass('active');
            updatePaymentTypeMessage($block, getActivePaymentType($block));
            refreshBlock($block);
        });

        // Payment type click.
        $block.find('#select-payment-type li').on('click', function (e) {
            e.preventDefault();
            $(this).parent().find('li').removeClass('active');
            $(this).addClass('active');
            updatePaymentTypeMessage($block, $(this).data('paymentType'));
            refreshBlock($block);
        });

        // Mobile package buttons.
        $block.find('.offer-35__select.package li').on('click', function (e) {
            e.preventDefault();
            $(this).parent().find('li').removeClass('active');
            $(this).addClass('active');
            const packageId = $(this).data('packageId');
            $block.find('.offer-35__column').each(function () {
                if ($(this).data('package') === packageId - 1) {
                    $(this).addClass('active');
                } else {
                    $(this).removeClass('active');
                }
            });
            scrollToColumns($block);
        });

        // AJAX add-to-cart.
        $block.on('click', '.offer-35__add-to-cart', function (e) {
            e.preventDefault();
            const $btn = $(this);
            const variationId = $btn.attr('data-product_id');
            // NOTE: never set prop('disabled') on the <a> — jQuery skips delegated
            // click handlers on disabled elements, so a second rapid click would no
            // longer be intercepted and the browser would follow the href
            // (?add-to-cart=ID) as a full navigation. Guard re-entry with the class.
            if (!variationId || $btn.hasClass('is-loading')) return;

            $btn.addClass('is-loading');

            dnnzAjax('offer35_add_to_cart', {
                main_variation_id: variationId,
            }).then(function (response) {
                return response.json();
            }).then(function (response) {
                if (response.success && response.data && response.data.redirect) {
                    window.location.href = response.data.redirect;
                } else {
                    $btn.removeClass('is-loading');
                }
            }).catch(function () {
                $btn.removeClass('is-loading');
            });
        });

        // Initial sync.
        refreshBlock($block);
    }

    $(document).ready(function () {
        $('.offer-35').each(function () {
            initBlock($(this));
        });
    });

    // Sticky package selector.
    // Observe a 1px sentinel placed before the wrapper instead of the wrapper itself —
    // the sentinel's flow position is stable (not affected by heading collapse), so the
    // "stuck" toggle doesn't ping-pong on the threshold.
    document.querySelectorAll('.offer-35 .package-select-wrapper').forEach(function (elem) {
        const stickyTop = parseInt(window.getComputedStyle(elem).getPropertyValue('top')) || 0;
        const sentinel = document.createElement('div');
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.cssText = 'height:1px;pointer-events:none;visibility:hidden;';
        elem.parentNode.insertBefore(sentinel, elem);

        new IntersectionObserver(function (entries) {
            for (const entry of entries) {
                elem.classList.toggle('stuck', !entry.isIntersecting);
            }
        }, {
            rootMargin: `-${stickyTop}px 0px 0px 0px`,
            threshold: 0,
        }).observe(sentinel);
    });

    function adjustColumnTopHeights() {
        const $columnTop = $('.offer-35__column-top');
        const $columnTopPrices = $('.offer-35__column-top .prices');
        const $subheadings = $('.offer-35__column-top .subheading');

        $columnTop.css('min-height', '');
        $columnTopPrices.css('min-height', '');
        $subheadings.css('min-height', '');
        $columnTopPrices.css('margin-top', '');

        // Below xl only one column is visible at a time (others are display:none and
        // measure 0), so equalizing would freeze the active column's height on all.
        if (window.innerWidth < 1200) return;

        let max = 0;
        $subheadings.each(function () { if ($(this).innerHeight() > max) max = $(this).innerHeight(); });
        $subheadings.css('min-height', max);

        // Align the start of the prices block: content above it (heading, promo badge,
        // subheading) can differ per column, so push shorter columns down by the difference.
        const offsets = $columnTopPrices.map(function () {
            return this.getBoundingClientRect().top - $(this).closest('.offer-35__column-top')[0].getBoundingClientRect().top;
        }).get();
        const maxOffset = Math.max.apply(null, offsets);
        $columnTopPrices.each(function (i) {
            const diff = maxOffset - offsets[i];
            if (diff > 0) {
                $(this).css('margin-top', 'calc(' + $(this).css('margin-top') + ' + ' + diff + 'px)');
            }
        });

        max = 0;
        $columnTopPrices.each(function () { if ($(this).innerHeight() > max) max = $(this).innerHeight(); });
        $columnTopPrices.css('min-height', max);
    }

    $(document).ready(adjustColumnTopHeights);
    $(window).on('resize', adjustColumnTopHeights);

    // Tooltip / modal handlers (shared with offer-30 modal).
    $('body').on('click', function (e) {
        if ($(e.target).parents().hasClass('property') || $(e.target).hasClass('property')) {
            e.stopPropagation();
            return;
        }
        $('.tooltip-open').remove();
        $('.offer-35 .tooltip').removeClass('open');
    });

    const $propertyElements = $('.offer-35 .property, .offer-35 .property-group-heading');

    $propertyElements.on('click', function (e) {
        openTooltip($(this).find('.tooltip'), e);
    });

    if (window.innerWidth > 992) {
        $propertyElements.on('mouseover', function (e) {
            openTooltip($(this).find('.tooltip'), e);
        });
    }

    $propertyElements.on('mouseout', function (event) {
        const e = event.toElement || event.relatedTarget;
        if ($(e).hasClass('tooltip-open') || $(e).parents().hasClass('tooltip-open')) return;
        $('.offer-35 .tooltip').removeClass('open');
        $('.tooltip-open').remove();
    });

    function openTooltip($tt, event) {
        if (!$tt || $tt.length === 0) return;
        if (event.type === 'click' && $(event.target).hasClass('play-icon') && $tt.hasClass('open')) {
            $('.tooltip-video').trigger('click');
            return;
        }
        if ($tt.hasClass('open')) return;
        $('.tooltip-open').remove();
        $('.offer-35 .tooltip').removeClass('open');
        $tt.addClass('open');

        const tooltipContent = $tt.data('tooltip-content');
        const tooltipVideo = $tt.data('video');
        const $tooltip = $(`${tooltipContent}`);
        $tt.append($tooltip);

        $tooltip.find('.tooltip-video').on('click', function () {
            $('#offer-30-video-modal video').attr('src', tooltipVideo).attr('controls', 'controls');
            $('#offer-30-video-modal video')[0].play();
            $('#offer-30-video-modal').fadeIn(300);
            $('body').css('overflow', 'hidden');
            $('.tooltip-open').remove();
            $tt.removeClass('open');
        });
    }
})(jQuery);
